import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage,
} from "../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { handlePostAction } from "../../utils/event-api-invoke";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../store/store-selector";
import { AppUtility } from "../../utils/utility";
import {UserRoles} from "../../AppConstants";

const UserRoleListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [userRoleRecordList, setUserRoleRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalUserRoles, setTotalUserRoles] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon to create new userRole
  const handleCreateUserRoleClick = () => {
    navigate("/userRole");
  };

  //on click of edit icon of userRole list
  const handleEditUserRoleClick = async (param) => {
    let _navigationData = param.recordResponse;
    //removing super admin
    _navigationData.roles = _navigationData.roles.filter(role => role.UserRoleCode !== UserRoles.SuperAdmin);
    _navigationData.roles.forEach(role => {
      delete role.CreatedDate;
      delete role.ModifiedDate;
      delete role.CountryCode;
      delete role.BusinessCode;
    });
    navigate(
      {
        pathname: "/userRole",
        search: createSearchParams({
          userEmail: param.recordResponse.UserEmail,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //on click of confirm button on delete popup
  const handleConfirm = async () => {
    deleteUserRoleRecord(selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel button on delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //on click of delete icon in list screen
  const handleDeleteUserRoleClick = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page size change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/

  //API call to delete userRole
  const deleteUserRoleRecord = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let _postRequest = {
        countryCode: settingsConfig.countryCode,
        businessCode: settingsConfig.businessCode,
        //remove super admin from list
        userRoleMapping: param.recordResponse.roles
        .filter(role => role.UserRoleCode !== UserRoles.SuperAdmin)
        .map(role => ({
          UserRoleMappingId: role.UserRoleMappingId,
          UserEmail: role.UserEmail,
          Deleted: true,
        }))      
      };
      
      let res = await handlePostAction(_postRequest, "userrole");
      if (res && res.status && res.status === 200) {
        notifyToast("User role deleted successfully", ToastType.SUCCESS);
        await FetchUserRoleList();;
      } else {
        notifyToast("Failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

//set list in grid

  const setUserRoleList = (userRoleList) => {
    try {
      userRoleList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _userRoleList = userRoleList.map((item,index) => {
        return {
          id: index,
          userEmail: item.UserEmail,
          role: item.roles.map(role => role.UserRoleDescription).join(', '),
          recordResponse: item,
        };
      });

      setUserRoleRecordList(_userRoleList);
      setTotalUserRoles(_userRoleList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

   //API call for userRole list
  const FetchUserRoleList = async () => {
    try {
      let _lists = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getAllUserWithRolesList",
        "useroles",
        "&role=sa"
      );
      setUserRoleList(_lists);
    } catch (err) {
      alert(err);
    }
  };
  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "userEmail",
      headerName: t("userRoleList.userEmail"),
      flex: 2,
    },
    {
      field: "role",
      headerName: t("userRoleList.role"),
      flex: 3,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const roles = params.row.recordResponse.roles;
        return roles.length !== 1 || roles[0].UserRoleCode !== UserRoles.SuperAdmin ? (
          <GridAction actionsConfig={actionConfigs} row={params.row} table={"userRole"}/>
        ) : null;
    },
  }
  ];

  //button config for add and export
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateUserRoleClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action:"create",
      table:"userrole"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(userRoleRecordList, "userRolelist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action:"download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      icon: MdCreate,
      onClick: handleEditUserRoleClick,
      buttonColor: "#EEEEE",
      action : "edit"
    },
    {
      icon: MdDelete,
      onClick: handleDeleteUserRoleClick,
      buttonColor: "#A62B23",
      action : "delete"
    },
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/

  useEffect(() => {
    spinnerCtx.setLoadingIndicator(true);
    FetchUserRoleList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={userRoleRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalUserRoles}
        toolbarHeader={t("userRoleList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteUserRoleConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default UserRoleListForm;
